import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import PropTypes from 'prop-types'
import { DeleteButton, Link, Subtitle, Table, Title } from '~/components'
import { useCanEdit } from '~/hooks'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'

const TreeAsList = (props) => {
  const canEdit = useCanEdit()
  const { editable = canEdit } = props
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { brand, action, category, subCategory } = useRouteContext(
    TreeAsList.name
  )
  const navigate = useNavigate()

  const brandCategories = useSelector((state) =>
    bshSlice.categories(state, brand.categories)
  )
  const subCategories = useSelector((state) =>
    bshSlice.categories(state, category?.categories)
  )

  const products = useSelector((state) =>
    bshSlice.products(state, subCategory?.products)
  )

  const [deletingCategory, setDeletingCategory] = useState()

  const title = useMemo(() => {
    if (!category) {
      return brand.name
    } else {
      return category.name
    }
  }, [brand.name, category])

  const subtitle = useMemo(() => {
    return t(
      subCategory
        ? 'ui.products-list'
        : category
          ? 'ui.product-subgroups'
          : 'ui.product-groups'
    )
  }, [category, subCategory, t])

  const urlPrefix = useMemo(() => {
    return `/${brand.url}/${action.path}`
  }, [action.path, brand.url])

  const rows = useMemo(() => {
    return subCategory ? products : category ? subCategories : brandCategories
  }, [brandCategories, category, products, subCategories, subCategory])

  const tableId = useMemo(() => {
    return `tree-as-list-${category ? 'category' : 'root'}`
  }, [category])

  return (
    <>
      <Title
        actions={
          category && canEdit ? (
            <DeleteButton
              dialog={{
                title: t('category.delete.confirm.title'),
                body: t('category.delete.confirm.body'),
              }}
              loading={category.id === deletingCategory}
              title={t('category.delete.cta')}
              onConfirmed={async () => {
                setDeletingCategory(category.id)
                await dispatch(bshSlice.removeCategory({ data: category }))
                navigate(urlPrefix, { replace: true })
                setDeletingCategory()
                return true
              }}
            />
          ) : undefined
        }
      >
        {title}
      </Title>
      <Subtitle sx={{ ':first-letter': { textTransform: 'uppercase' } }}>
        {subtitle}
      </Subtitle>
      {rows && (
        <Table
          key={tableId}
          index
          headers={[
            {
              content: t(
                subCategory
                  ? 'product.name'
                  : category
                    ? 'ui.subgroups'
                    : 'ui.groups'
              ),
            },
          ]}
          hover
          rows={rows.map((row) => {
            const cells = [
              {
                content: (
                  <Link
                    className="rounded-sm"
                    to={`${urlPrefix}/${row.model}/${row.id}`}
                    sx={{
                      backgroundColor: (theme) => {
                        return theme.palette.background.button
                      },
                      color: 'inherit',
                      display: 'block',
                      padding: '0.25rem 0.5rem',
                      textDecoration: 'none',
                    }}
                  >
                    {row.name || '<empty>'}
                  </Link>
                ),
              },
            ]
            if (editable) {
              cells.push({
                content: (
                  <DeleteButton
                    dialog={{
                      title: t('category.delete.confirm.title'),
                      body: t('category.delete.confirm.body'),
                    }}
                    loading={row.id === deletingCategory}
                    title={t('category.delete.cta')}
                    onConfirmed={async () => {
                      setDeletingCategory(row.id)
                      await dispatch(bshSlice.removeCategory({ data: row }))
                      setDeletingCategory()
                      return true
                    }}
                  />
                ),
                narrow: true,
              })
            }
            return { cells }
          })}
          tableId={tableId}
        />
      )}
    </>
  )
}

TreeAsList.propTypes = {
  editable: PropTypes.bool,
}

export default TreeAsList
